import "../styles/admin-base.css";
import "../styles/tickets.css";
import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import {
  ArrowLeft,
  Send,
  Clock,
  User,
  Shield,
  AlertCircle,
  CheckCircle2,
  Building2,
  ChevronDown,
  Check,
} from "lucide-react";
import api from "../api/admin";
import { createTicketSocket, joinTicketRoom, leaveTicketRoom } from "../../src/realtime/ticketSocket";

const STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "ticket_assigned", label: "Ticket Assigned" },
  { value: "in_progress", label: "In Progress" },
  { value: "closed", label: "Closed" },
];

const STATUS_TONES = {
  open: "is-orange",
  ticket_assigned: "is-purple",
  in_progress: "is-blue",
  closed: "is-green",
};

const PRIORITY_TONES = {
  urgent: "is-red",
  high: "is-orange",
  medium: "is-blue",
  low: "is-green",
};

const DEPARTMENT_OPTIONS = [
  { key: "medical", label: "Medical Department" },
  { key: "legal", label: "Legal Department" },
  { key: "education", label: "Education Department" },
  { key: "emergency-support", label: "Emergency Support" },
  { key: "finance", label: "Finance Department" },
];

const DEPT_KEY_TO_ACCOUNT_DEPT = {
  medical: "medical",
  legal: "legal",
  education: "education",
  "emergency-support": "emergency",
  finance: null,
};

const DEPT_DISPLAY_NAMES = {
  legal: "Legal Dept.",
  medical: "Medical Dept.",
  education: "Education Dept.",
  emergency: "Emergency Dept.",
};

const DEPT_ROLE_NAMES = {
  legal: "Legal Department",
  medical: "Medical Department",
  education: "Education Department",
  emergency: "Emergency Support",
  finance: "Finance Department",
};

const getTicketDeptKey = (ticket) => {
  if (!ticket) return null;
  if (ticket.departmentAssignment?.key) return ticket.departmentAssignment.key;
  if (ticket.department) {
    const match = DEPARTMENT_OPTIONS.find(
      (d) => d.label === ticket.department || d.key === ticket.department
    );
    return match?.key || null;
  }
  return null;
};

const toTitleCase = (value) =>
  value
    ? value.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())
    : "—";

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const isDepartmentAssignmentMessage = (message) => {
  if (typeof message !== "string") return false;
  try {
    const parsed = JSON.parse(message);
    return parsed?.type === "department-assignment";
  } catch (_e) {
    return false;
  }
};

const toNumericId = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

const normalizeSenderRole = (message) => {
  const rawRole = message?.senderRole || message?.sender_role || message?.authorType;
  if (!rawRole) {
    if (message?.authorDeptAccountId || message?.authorDeptId) return "department";
    if (message?.authorAdminId || message?.isAdmin) return "admin";
    if (message?.authorUserId || message?.isUser) return "user";
    return "system";
  }
  const role = String(rawRole).toLowerCase();
  if (role === "dept") return "department";
  if (role.includes("department")) return "department";
  if (role.includes("admin")) return "admin";
  if (role.includes("user") || role.includes("member") || role === "client") return "user";
  return "system";
};

const formatAdminRole = (role) => {
  if (!role) return "Administrator";
  const normalized = String(role).trim().toLowerCase();
  if (normalized.includes("super")) return "Super Administrator";
  if (normalized.includes("ticket")) return "Ticket Administrator";
  return toTitleCase(normalized);
};

const getAdminRoleTone = (role) => {
  if (!role) return "default";
  const normalized = String(role).trim().toLowerCase();
  if (normalized.includes("super")) return "super";
  if (normalized.includes("ticket")) return "ticket";
  return "default";
};

const formatDepartmentRole = (department) => {
  if (!department) return "Department";
  const normalized = String(department).trim().toLowerCase();
  return DEPT_ROLE_NAMES[normalized] || toTitleCase(normalized);
};

const resolveMessagePresenter = (message, currentStaffAuthorIds) => {
  const senderRole = normalizeSenderRole(message);
  const isSystem = senderRole === "system" || message.message?.startsWith("[System]");

  if (isSystem) {
    return {
      senderType: "system",
      senderLabel: "System",
      senderName: "System",
      senderRoleLabel: null,
      adminRoleTone: null,
      isOutgoingStaffMessage: false,
    };
  }

  if (senderRole === "department") {
    const deptId = toNumericId(
      message.authorDeptAccountId ?? message.authorDeptId ?? message.senderId ?? message.sender_id
    );
    return {
      senderType: "dept",
      senderLabel: `${message.authorDeptName || "Department Handler"} | ${formatDepartmentRole(message.authorDeptDepartment)}`,
      senderName: message.authorDeptName || "Department Handler",
      senderRoleLabel: formatDepartmentRole(message.authorDeptDepartment),
      adminRoleTone: null,
      isOutgoingStaffMessage: deptId != null && currentStaffAuthorIds.deptIds.has(deptId),
    };
  }

  if (senderRole === "admin") {
    const adminId = toNumericId(message.authorAdminId ?? message.senderId ?? message.sender_id);
    return {
      senderType: "admin",
      senderLabel: `${message.authorAdminName || "Admin"} | ${formatAdminRole(message.authorAdminRole)}`,
      senderName: message.authorAdminName || "Admin",
      senderRoleLabel: formatAdminRole(message.authorAdminRole),
      adminRoleTone: getAdminRoleTone(message.authorAdminRole),
      isOutgoingStaffMessage: adminId != null && currentStaffAuthorIds.adminIds.has(adminId),
    };
  }

  return {
    senderType: "member",
    senderLabel: message.authorUserName || message.authorUserId || "Member",
    senderName: message.authorUserName || message.authorUserId || "Member",
    senderRoleLabel: null,
    adminRoleTone: null,
    isOutgoingStaffMessage: false,
  };
};

export default function TicketDetail() {
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [replyText, setReplyText] = useState("");
  const [replySending, setReplySending] = useState(false);
  const [replyError, setReplyError] = useState("");

  const [statusUpdating, setStatusUpdating] = useState(false);
  const [handlerUpdating, setHandlerUpdating] = useState(false);
  const [deptAccounts, setDeptAccounts] = useState([]);
  const [openActionMenu, setOpenActionMenu] = useState(null);

  const messagesEndRef = useRef(null);
  const socketRef = useRef(null);

  const ticketId = new URLSearchParams(window.location.search).get("id");

  const currentStaffAuthorIds = useMemo(() => {
    if (typeof window === "undefined" || !window.localStorage) {
      return { adminIds: new Set(), deptIds: new Set() };
    }
    try {
      const raw = window.localStorage.getItem("adminUser");
      const parsed = raw ? JSON.parse(raw) : null;
      const toValidId = (value) => {
        const numeric = Number(value);
        return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
      };

      const adminCandidates = [parsed?.id, parsed?.adminId, parsed?.admin?.id]
        .map(toValidId)
        .filter(Boolean);
      const deptCandidates = [
        parsed?.departmentAccountId,
        parsed?.deptAccountId,
        parsed?.departmentAccount?.id,
        parsed?.departmentAccount?.deptAccountId,
        parsed?.deptAccount?.id,
      ]
        .map(toValidId)
        .filter(Boolean);

      return {
        adminIds: new Set(adminCandidates),
        deptIds: new Set(deptCandidates),
      };
    } catch (_error) {
      return { adminIds: new Set(), deptIds: new Set() };
    }
  }, []);

  const loadTicket = useCallback(async () => {
    if (!ticketId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await api.getTicket(ticketId);
      setTicket(res.data.ticket || res.data || null);
      const msgs = (res.data.messages || []).filter(
        (m) => m.message && !isDepartmentAssignmentMessage(m.message)
      );
      setMessages(msgs);
    } catch (err) {
      console.error("Unable to load ticket", err);
      setError("Unable to load ticket details.");
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      await loadTicket();
      if (!mounted) return;
    };
    run();
    return () => {
      mounted = false;
    };
  }, [loadTicket]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    const activeTicketId = ticket?.id ?? ticketId;
    if (!activeTicketId) {
      return undefined;
    }

    const socket = createTicketSocket("admin");
    if (!socket) {
      return undefined;
    }

    socketRef.current = socket;

    const handleReceiveMessage = (incomingMessage) => {
      if (String(incomingMessage?.ticketId ?? "") !== String(activeTicketId)) {
        return;
      }

      if (isDepartmentAssignmentMessage(incomingMessage?.message)) {
        return;
      }

      setMessages((previousMessages) => {
        const alreadyExists = previousMessages.some((message) => String(message.id) === String(incomingMessage.id));
        if (alreadyExists) {
          return previousMessages;
        }
        return [...previousMessages, incomingMessage];
      });
    };

    const joinActiveTicketRoom = () => {
      joinTicketRoom(socket, activeTicketId);
    };

    socket.on("connect", joinActiveTicketRoom);
    socket.on("receive_message", handleReceiveMessage);
    joinActiveTicketRoom();

    return () => {
      leaveTicketRoom(socket, activeTicketId);
      socket.off("connect", joinActiveTicketRoom);
      socket.off("receive_message", handleReceiveMessage);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [ticket?.id, ticketId]);

  useEffect(() => {
    const fetchDeptAccounts = async () => {
      try {
        const res = await api.listDepartmentAccounts();
        setDeptAccounts(res.data?.departmentAccounts || []);
      } catch (_e) {
        /* non-critical */
      }
    };
    fetchDeptAccounts();
  }, []);

  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (!event.target.closest(".ticket-detail-dropdown")) {
        setOpenActionMenu(null);
      }
    };
    document.addEventListener("click", handleDocumentClick);
    return () => document.removeEventListener("click", handleDocumentClick);
  }, []);

  const handleSendReply = async () => {
    const trimmed = replyText.trim();
    if (!trimmed || !ticket) return;

    setReplySending(true);
    setReplyError("");
    try {
      const activeAdminId = Array.from(currentStaffAuthorIds.adminIds)[0] || null;
      const payload = activeAdminId
        ? { message: trimmed, authorAdminId: activeAdminId }
        : { message: trimmed };
      await api.postTicketMessage(ticket.id, payload);
      setReplyText("");
    } catch (err) {
      console.error("Failed to send reply:", err);
      setReplyError("Failed to send reply. Please try again.");
    } finally {
      setReplySending(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!ticket || statusUpdating) return;
    setStatusUpdating(true);
    try {
      await api.updateTicket(ticket.id, { status: newStatus });
      setTicket((prev) => (prev ? { ...prev, status: newStatus } : prev));
    } catch (err) {
      console.error("Failed to update status:", err);
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleAssignHandler = async (deptAccountId) => {
    if (!ticket || handlerUpdating) return;
    setHandlerUpdating(true);
    try {
      const res = await api.assignTicketHandler(ticket.id, {
        assignedDeptAccountId: Number(deptAccountId),
      });
      // Update status from response if auto-changed (e.g. open -> ticket_assigned)
      if (res.data?.ticket?.status) {
        setTicket((prev) => prev ? { ...prev, status: res.data.ticket.status } : prev);
      }
      await loadTicket();
    } catch (err) {
      console.error("Failed to assign handler:", err);
    } finally {
      setHandlerUpdating(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendReply();
    }
  };

  const handlerOptions = useMemo(() => {
    const deptKey = getTicketDeptKey(ticket);
    const accountDept = deptKey ? DEPT_KEY_TO_ACCOUNT_DEPT[deptKey] : null;
    const filtered = accountDept
      ? deptAccounts.filter((a) => a.department === accountDept)
      : deptAccounts;
    return filtered.map((a) => ({
      value: String(a.id),
      label: `${DEPT_DISPLAY_NAMES[a.department] || a.department} | ${a.fullName || a.email}`,
    }));
  }, [deptAccounts, ticket]);

  if (loading) {
    return (
      <div className="admin-page ticket-detail-page">
        <div className="admin-empty-state">
          <p>Loading ticket details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-page ticket-detail-page">
        <div className="admin-empty-state">
          <AlertCircle size={24} />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="admin-page ticket-detail-page">
        <div className="admin-empty-state">
          <p>
            No ticket selected. Open a ticket from the Tickets list or add
            ?id=&lt;ticketId&gt; to the URL.
          </p>
        </div>
      </div>
    );
  }

  const priorityTone = PRIORITY_TONES[ticket.priority] || "is-blue";
  const isClosedOrResolved = ticket.status === "closed";
  const ticketNumberLabel = ticket.ticketNo ?? ticket.ticket_no ?? `#${ticket.id}`;
  const statusLabel = STATUS_OPTIONS.find((option) => option.value === ticket.status)?.label || toTitleCase(ticket.status);

  return (
    <div className="admin-page admin-stack-lg ticket-detail-page">
      {/* Header */}
      <header className="admin-surface ticket-detail-hero">
        <div className="ticket-detail-hero__intro">
          <button
            type="button"
            className="ticket-detail-hero__back-link"
            onClick={() => window.history.back()}
          >
            <ArrowLeft size={18} />
            Back to Tickets
          </button>
          <div>
            <span className="ticket-detail-hero__eyebrow">Support Ticket Workspace</span>
            <h1 className="ticket-detail__title">Ticket Detail</h1>
            <p className="admin-muted ticket-detail__subtitle">
              {ticketNumberLabel} —{" "}
              {ticket.subject}
            </p>
          </div>
        </div>
      </header>

      {/* Info Cards + Actions Row */}
      <div className="ticket-detail__grid">
        {/* Ticket Info */}
        <section className="admin-surface admin-stack-md ticket-detail-section ticket-detail-section--info">
          <h3 className="ticket-detail__section-title">
            Ticket Information
          </h3>
          <div className="admin-detail-grid">
            <div className="admin-card ticket-detail-card">
              <div className="admin-card__label">Category</div>
              <div className="admin-card__value">{toTitleCase(ticket.category)}</div>
            </div>
            <div className="admin-card ticket-detail-card">
              <div className="admin-card__label">Priority</div>
              <div className="admin-card__value">
                <span className={`admin-pill ${priorityTone}`}>
                  {toTitleCase(ticket.priority)}
                </span>
              </div>
            </div>
            <div className="admin-card ticket-detail-card">
              <div className="admin-card__label">Member</div>
              <div className="admin-card__value">
                {ticket.user?.name || ticket.userName || ticket.userEmail || "Member"}
              </div>
              <div className="admin-card__meta">
                {ticket.user?.email || ticket.userEmail || "—"}
              </div>
            </div>
            <div className="admin-card ticket-detail-card">
              <div className="admin-card__label">Member ID</div>
              <div className="admin-card__value">
                {ticket.membershipNo || ticket.user?.membershipNo || "—"}
              </div>
            </div>
            <div className="admin-card ticket-detail-card">
              <div className="admin-card__label">Created</div>
              <div className="admin-card__value">
                {formatDate(ticket.createdAt || ticket.created_at)}
              </div>
            </div>
            {ticket.slaDeadline && (
              <div className="admin-card ticket-detail-card">
                <div className="admin-card__label">
                  <Clock size={14} style={{ verticalAlign: "middle", marginRight: "4px" }} />
                  SLA Deadline
                </div>
                <div className="admin-card__value">
                  {formatDate(ticket.slaDeadline)}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Admin Actions */}
        <section className="admin-surface admin-stack-md ticket-detail-section ticket-detail-section--actions">
          <h3 className="ticket-detail__section-title">
            Actions
          </h3>

          {/* Status Change */}
          <div className={`ticket-detail__action-card ${openActionMenu === "status" ? "is-open" : ""}`}>
            <label className="ticket-detail__action-label">
              Update Status
            </label>
            <div className={`ticket-detail-dropdown ${openActionMenu === "status" ? "is-open" : ""}`}>
              <button
                type="button"
                className="ticket-detail-dropdown__trigger"
                onClick={() => setOpenActionMenu((prev) => (prev === "status" ? null : "status"))}
                disabled={statusUpdating}
                aria-haspopup="listbox"
                aria-expanded={openActionMenu === "status"}
              >
                <span>{statusLabel}</span>
                <ChevronDown size={16} />
              </button>

              {openActionMenu === "status" ? (
                <div className="ticket-detail-dropdown__menu" role="listbox" aria-label="Update Status">
                  {STATUS_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`ticket-detail-dropdown__option ${ticket.status === opt.value ? "is-active" : ""}`}
                      onClick={() => {
                        setOpenActionMenu(null);
                        if (opt.value !== ticket.status) {
                          handleStatusChange(opt.value);
                        }
                      }}
                    >
                      <span>{opt.label}</span>
                      {ticket.status === opt.value ? <Check size={14} /> : null}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          {/* Handler Assignment */}
          <div className={`ticket-detail__action-card ${openActionMenu === "handler" ? "is-open" : ""}`}>
            <label className="ticket-detail__action-label">
              Assign Handler
              {ticket.assignedDeptAccount
                ? ` (Current: ${ticket.assignedDeptAccount.name || ticket.assignedDeptAccount.email})`
                : ""}
            </label>
            <div className={`ticket-detail-dropdown ${openActionMenu === "handler" ? "is-open" : ""}`}>
              <button
                type="button"
                className="ticket-detail-dropdown__trigger"
                onClick={() => setOpenActionMenu((prev) => (prev === "handler" ? null : "handler"))}
                disabled={handlerUpdating}
                aria-haspopup="listbox"
                aria-expanded={openActionMenu === "handler"}
              >
                <span>
                  {ticket.assignedDeptAccount
                    ? `${DEPT_DISPLAY_NAMES[ticket.assignedDeptAccount.department] || toTitleCase(ticket.assignedDeptAccount.department)} | ${ticket.assignedDeptAccount.name || ticket.assignedDeptAccount.email}`
                    : "Select handler..."}
                </span>
                <ChevronDown size={16} />
              </button>

              {openActionMenu === "handler" ? (
                <div className="ticket-detail-dropdown__menu" role="listbox" aria-label="Assign Handler">
                  {handlerOptions.length === 0 ? (
                    <div className="ticket-detail-dropdown__empty">No handlers available.</div>
                  ) : (
                    handlerOptions.map((opt) => {
                      const selectedId = String(ticket.assignedDeptAccount?.id ?? "");
                      const isActive = selectedId === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          className={`ticket-detail-dropdown__option ${isActive ? "is-active" : ""}`}
                          onClick={() => {
                            setOpenActionMenu(null);
                            if (!isActive) {
                              handleAssignHandler(opt.value);
                            }
                          }}
                        >
                          <span>{opt.label}</span>
                          {isActive ? <Check size={14} /> : null}
                        </button>
                      );
                    })
                  )}
                </div>
              ) : null}
            </div>
          </div>

          {ticket.aiResponse && (
            <div className="admin-card ticket-detail__ai-card">
              <div className="admin-card__label">AI Summary</div>
              <div className="admin-card__value ticket-detail__ai-value">
                {typeof ticket.aiResponse === "string"
                  ? ticket.aiResponse
                  : ticket.aiResponse.message || "—"}
              </div>
            </div>
          )}
        </section>
      </div>

      {/* Conversation Thread */}
      <section className="admin-surface tkt-convo ticket-detail-section ticket-detail-section--conversation">
        <h3 className="tkt-convo__title">
          Conversation
        </h3>

        <div className="tkt-convo__thread">
          {messages.length === 0 && (
            <div className="admin-empty-state">
              <p>No messages yet. Send the first reply below.</p>
            </div>
          )}

          {messages.map((m) => {
            const presenter = resolveMessagePresenter(m, currentStaffAuthorIds);
            const isOutgoingAdminMessage = presenter.isOutgoingStaffMessage;
            const isSystem = presenter.senderType === "system";
            const displayMessage = m.message
              ?.replace(/^\[Category: [^\]]+\]\s*/, "")
              ?.replace(/^\[System\]\s*/, "");

            const bodyClass = presenter.senderType === "dept"
              ? "tkt-msg__body tkt-msg__body--dept"
              : presenter.senderType === "admin"
              ? `tkt-msg__body tkt-msg__body--admin tkt-msg__body--admin-${presenter.adminRoleTone || "default"}`
              : isSystem
              ? "tkt-msg__body tkt-msg__body--system"
              : "tkt-msg__body tkt-msg__body--member";

            const avatarClass = presenter.senderType === "dept"
              ? "tkt-msg__avatar tkt-msg__avatar--dept"
              : presenter.senderType === "admin"
              ? `tkt-msg__avatar tkt-msg__avatar--admin tkt-msg__avatar--admin-${presenter.adminRoleTone || "default"}`
              : isSystem
              ? "tkt-msg__avatar tkt-msg__avatar--system"
              : "tkt-msg__avatar tkt-msg__avatar--member";

            return (
              <div
                key={m.id}
                className={`tkt-msg ${isOutgoingAdminMessage ? "tkt-msg--outgoing" : "tkt-msg--member"}`}
              >
                <div className={avatarClass}>
                  {presenter.senderType === "dept" ? (
                    <Building2 size={12} />
                  ) : presenter.senderType === "admin" ? (
                    <Shield size={12} />
                  ) : isSystem ? (
                    <AlertCircle size={12} />
                  ) : (
                    <User size={12} />
                  )}
                </div>
                <div className={bodyClass}>
                  <div className="tkt-msg__header">
                    <span className="tkt-msg__author">{presenter.senderName || presenter.senderLabel}</span>
                    {presenter.senderRoleLabel ? (
                      <span
                        className={`tkt-msg__author-role tkt-msg__author-role--${presenter.senderType}${
                          presenter.senderType === "admin"
                            ? ` tkt-msg__author-role--admin-${presenter.adminRoleTone || "default"}`
                            : ""
                        }`}
                      >
                        {presenter.senderRoleLabel}
                      </span>
                    ) : null}
                    <span className="tkt-msg__time">{formatDate(m.createdAt)}</span>
                  </div>
                  <div className="tkt-msg__text">{displayMessage}</div>
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        {/* Reply Composer */}
        {!isClosedOrResolved ? (
          <div className="tkt-reply">
            {replyError && (
              <div className="tkt-reply__error">
                {replyError}
              </div>
            )}
            <div className="tkt-reply__row">
              <textarea
                className="tkt-reply__textarea"
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your reply... (Enter to send, Shift+Enter for new line)"
                disabled={replySending}
                rows={3}
              />
              <button
                type="button"
                className="admin-button is-primary tkt-reply__send"
                onClick={handleSendReply}
                disabled={replySending || !replyText.trim()}
              >
                <Send size={16} />
                {replySending ? "Sending..." : "Send Reply"}
              </button>
            </div>
          </div>
        ) : (
          <div className="tkt-reply__closed">
            <CheckCircle2 size={16} />
            This ticket is {ticket.status}. Reopen it to send more messages.
          </div>
        )}
      </section>
    </div>
  );
}
